import { useEffect, useState } from "react";
import { useParams, Navigate, Link } from "react-router-dom";
import axios from "axios";
import { Clock, Tag, ArrowLeft, ArrowRight } from "lucide-react";
import { SeoHead } from "@/lib/seo";
import { Breadcrumb, breadcrumbJsonLd } from "@/components/Breadcrumb";
import BlogBody from "@/components/BlogBody";
import BlogCard from "@/components/BlogCard";
import { useLang } from "@/lib/lang-context";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MONTH_IT = [
  "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
];

function formatDate(iso, lang = "it") {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00Z");
  if (lang === "en") {
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  }
  return `${d.getUTCDate()} ${MONTH_IT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

const BlogPost = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setPost(null);
    setError(null);
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/blog/posts/${slug}`, {
          params: { lang },
        });
        if (!cancelled) setPost(data);
      } catch (e) {
        if (!cancelled) setError(e?.response?.status || "error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug, lang]);

  if (error === 404) {
    return <Navigate to="/blog" replace />;
  }

  if (!post) {
    return (
      <main className="bg-[#FBFAF7] min-h-screen flex items-center justify-center">
        <div className="font-mono text-sm text-[#6C706D]">
          {lang === "it" ? "Caricamento articolo…" : "Loading article…"}
        </div>
      </main>
    );
  }

  // Schema.org Article JSON-LD — rich result eligibility on Google.
  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image ? [post.cover_image] : undefined,
    datePublished: post.publish_date,
    dateModified: post.publish_date,
    author: { "@type": "Organization", name: post.author || "Deal Rating" },
    publisher: {
      "@type": "Organization",
      name: "Deal Rating",
      logo: { "@type": "ImageObject", url: "https://dealrating.it/logo.png" },
    },
  };

  return (
    <main className="bg-[#FBFAF7] min-h-screen">
      <SeoHead
        title={post.seo_title || `${post.title} · Deal Rating`}
        description={post.seo_description || post.excerpt}
        canonical={`/blog/${post.slug}`}
        ogImage={post.cover_image}
        jsonLd={[
          articleJsonLd,
          breadcrumbJsonLd([
            { label: "Home", to: "/" },
            { label: "Blog", to: "/blog" },
            { label: post.title, to: `/blog/${post.slug}` },
          ]),
        ]}
      />

      {/* Cover */}
      {post.cover_image && (
        <div
          className="w-full h-64 md:h-96 bg-cover bg-center"
          style={{ backgroundImage: `url(${post.cover_image})` }}
        />
      )}

      <article className="max-w-3xl mx-auto px-6 md:px-10 py-10">
        <Breadcrumb
          items={[
            { label: "Home", to: "/" },
            { label: "Blog", to: "/blog" },
            { label: post.title, to: `/blog/${post.slug}` },
          ]}
        />

        <header className="mt-8 mb-10">
          <div className="label-eyebrow text-[#4A5D23]">
            {formatDate(post.publish_date, lang)} ·{" "}
            {(post.tags || [])[0] || (lang === "it" ? "Editoriale" : "Editorial")}
          </div>
          <h1 className="font-serif-display text-4xl sm:text-5xl text-[#282C2A] leading-[1.05] mt-3">
            {post.title}
          </h1>
          <p className="mt-5 text-lg text-[#6C706D] leading-relaxed">{post.excerpt}</p>
          <div className="mt-6 flex items-center gap-4 text-xs font-mono uppercase tracking-wider text-[#6C706D] border-t border-b border-[#E6E4DD] py-3">
            <span>{post.author || "Deal Rating"}</span>
            {post.read_time_min && (
              <span className="inline-flex items-center gap-1.5">
                <Clock size={12} strokeWidth={1.5} />
                {post.read_time_min} min
              </span>
            )}
            <span className="ml-auto inline-flex items-center gap-1.5">
              <Tag size={12} strokeWidth={1.5} />
              {(post.tags || []).slice(0, 3).join(" · ")}
            </span>
          </div>
        </header>

        <BlogBody blocks={post.body} />

        <div className="mt-14 pt-8 border-t border-[#E6E4DD] flex items-center justify-between">
          <Link
            to="/blog"
            data-testid="blog-back-link"
            className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-wider text-[#6C706D] hover:text-[#282C2A]"
          >
            <ArrowLeft size={14} strokeWidth={1.75} />
            {lang === "it" ? "Tutti gli articoli" : "All articles"}
          </Link>
          <Link
            to="/"
            data-testid="blog-cta-analyze"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#4A5D23] text-white text-xs font-semibold tracking-wider uppercase border border-[#3B4A1C] hover:bg-[#3B4A1C] transition-colors"
          >
            {lang === "it" ? "Analizza un annuncio" : "Analyse a listing"}
            <ArrowRight size={14} strokeWidth={1.75} />
          </Link>
        </div>
      </article>

      {/* Related */}
      {post.related && post.related.length > 0 && (
        <section className="bg-[#F0EFEB] border-t border-[#E6E4DD] py-14">
          <div className="max-w-6xl mx-auto px-6 md:px-10">
            <div className="label-eyebrow text-[#6C706D] mb-3">
              {lang === "it" ? "Ti può interessare" : "You may also like"}
            </div>
            <h2 className="font-serif-display text-3xl text-[#282C2A] leading-tight mb-8">
              {lang === "it" ? "Continua a leggere" : "Keep reading"}
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {post.related.map((r) => (
                <BlogCard key={r.slug} post={r} lang={lang} />
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
};

export default BlogPost;
